import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import {
    getSalesReactBaseUrl,
    getLiveDataClients,
    getLiveDataBranches,
    vikrayaLoginAuthenticate,
    getVikrayaBillDetails,
    syncVikrayaCounterData,
    sendVikrayaSms,
    sendVikrayaSalesTrackingRecords,
    getVikrayaClientCountUpdation,
    getVikrayaApplicationSetting,
    callbackVikrayaApplicationSetting,
    getVikrayaMastersCreation,
    callbackVikrayaMastersCreation,
    getVikrayaCreditCustomer,
    callbackVikrayaCreditCustomer,
    getVikrayaOutletDetails,
    callbackVikrayaOutletDetails,
    getVikrayaProductCategory,
    callbackVikrayaProductCategory,
    getVikrayaProduct,
    callbackVikrayaProduct,
    getVikrayaUsers,
    callbackVikrayaUsers,
    getVikrayaPrinterDetails,
    callbackVikrayaPrinterDetails,
    getVikrayaNotifications,
    callbackVikrayaNotifications,
    getVikrayaCreditReport,
    getVikrayaLastBillDetails,
    getVikrayaItemsReport,
    getVikrayaSpecialProductCust,
    getVikrayaCustomerData,
    uploadVikrayaImage,
    getVikrayaRefInformation,
    getVikrayaPaymentCallback
} from '../utilities/api';

/** Handlers for endpoints that take only the branch/segment in the URL and no request body. */
const ENDPOINT_HANDLERS = {
    get_billdetails: getVikrayaBillDetails,
    send_sms: sendVikrayaSms,
    sales_tracking_records: sendVikrayaSalesTrackingRecords,
    get_client_countupdation: getVikrayaClientCountUpdation,
    Get_application_setting: getVikrayaApplicationSetting,
    Get_masters_creation: getVikrayaMastersCreation,
    Get_credit_customer: getVikrayaCreditCustomer,
    Get_outlet_deatils: getVikrayaOutletDetails,
    Get_product_category: getVikrayaProductCategory,
    Get_product: getVikrayaProduct,
    Get_users: getVikrayaUsers,
    Get_printer_details: getVikrayaPrinterDetails,
    Get_notifications: getVikrayaNotifications,
    get_credit_report: getVikrayaCreditReport,
    get_customer_data: getVikrayaCustomerData
};

/** Handlers for the three report endpoints that take a second path segment (cust_id / user_id). */
const EXTRA_PARAM_HANDLERS = {
    get_lastbill_details: getVikrayaLastBillDetails,
    get_items_report: getVikrayaItemsReport,
    special_product_cust: getVikrayaSpecialProductCust
};

/** POST callback handlers used to acknowledge (mark as synced) pending master data rows. */
const CALLBACK_HANDLERS = {
    callback_application_setting: callbackVikrayaApplicationSetting,
    callback_masters_creation: callbackVikrayaMastersCreation,
    callback_credit_customer: callbackVikrayaCreditCustomer,
    callback_outlet_deatils: callbackVikrayaOutletDetails,
    callback_product_category: callbackVikrayaProductCategory,
    callback_product: callbackVikrayaProduct,
    callback_users: callbackVikrayaUsers,
    callback_printer_details: callbackVikrayaPrinterDetails,
    callback_notifications: callbackVikrayaNotifications
};

// Every route below is Client\Vikraya — unlike GS Billing there is no server/client
// split, everything is served from the client's own deployment (client_creation.base_url).
const ENDPOINTS = [
    { key: 'login_authenticate', label: 'Login Authenticate', method: 'POST', path: 'api/vikraya_app/login_authenticate', group: 'Login & Bill Sync', noBranch: true },
    { key: 'get_billdetails', label: 'Get Bill Details', method: 'POST', path: 'api/vikraya_app/get_billdetails/{segment}', group: 'Login & Bill Sync' },
    { key: 'counterdata_synching', label: 'Counter Data Synching', method: 'POST', path: 'api/vikraya_app/counterdata_synching/{segment}', group: 'Login & Bill Sync' },
    { key: 'send_sms', label: 'Send SMS', method: 'POST', path: 'api/vikraya_app/send_sms/{segment}', group: 'Login & Bill Sync' },
    { key: 'sales_tracking_records', label: 'Sales Tracking Records', method: 'POST', path: 'api/vikraya_app/sales_tracking_records/{segment}', group: 'Login & Bill Sync' },

    { key: 'get_client_countupdation', label: 'Client Count Updation', method: 'GET', path: 'api/vikraya_app/get_client_countupdation/{segment}', group: 'Master Data Sync' },

    { key: 'Get_application_setting', label: 'Application Setting', method: 'GET', path: 'api/vikraya_app/Get_application_setting/{segment}', group: 'Master Data Sync' },
    { key: 'callback_application_setting', label: 'Callback Application Setting', method: 'POST', path: 'api/vikraya_app/callback_application_setting/{segment}', group: 'Master Data Sync', isCallback: true, sourceKey: 'Get_application_setting', idField: 'app_setting_id' },

    { key: 'Get_masters_creation', label: 'Masters Creation', method: 'GET', path: 'api/vikraya_app/Get_masters_creation/{segment}', group: 'Master Data Sync' },
    { key: 'callback_masters_creation', label: 'Callback Masters Creation', method: 'POST', path: 'api/vikraya_app/callback_masters_creation/{segment}', group: 'Master Data Sync', isCallback: true, sourceKey: 'Get_masters_creation', idField: 'masterId' },

    { key: 'Get_credit_customer', label: 'Credit Customer', method: 'GET', path: 'api/vikraya_app/Get_credit_customer/{segment}', group: 'Master Data Sync' },
    { key: 'callback_credit_customer', label: 'Callback Credit Customer', method: 'POST', path: 'api/vikraya_app/callback_credit_customer/{segment}', group: 'Master Data Sync', isCallback: true, sourceKey: 'Get_credit_customer', idField: 'cust_id' },

    { key: 'Get_outlet_deatils', label: 'Outlet Details', method: 'GET', path: 'api/vikraya_app/Get_outlet_deatils/{segment}', group: 'Master Data Sync' },
    { key: 'callback_outlet_deatils', label: 'Callback Outlet Details', method: 'POST', path: 'api/vikraya_app/callback_outlet_deatils/{segment}', group: 'Master Data Sync', isCallback: true, sourceKey: 'Get_outlet_deatils', idField: 'outId' },

    { key: 'Get_product_category', label: 'Product Category', method: 'GET', path: 'api/vikraya_app/Get_product_category/{segment}', group: 'Master Data Sync' },
    { key: 'callback_product_category', label: 'Callback Product Category', method: 'POST', path: 'api/vikraya_app/callback_product_category/{segment}', group: 'Master Data Sync', isCallback: true, sourceKey: 'Get_product_category', idField: 'pr_cat_id' },

    { key: 'Get_product', label: 'Product', method: 'GET', path: 'api/vikraya_app/Get_product/{segment}', group: 'Master Data Sync' },
    { key: 'callback_product', label: 'Callback Product', method: 'POST', path: 'api/vikraya_app/callback_product/{segment}', group: 'Master Data Sync', isCallback: true, sourceKey: 'Get_product', idField: 'pr_id' },

    { key: 'Get_users', label: 'Users', method: 'GET', path: 'api/vikraya_app/Get_users/{segment}', group: 'Master Data Sync' },
    { key: 'callback_users', label: 'Callback Users', method: 'POST', path: 'api/vikraya_app/callback_users/{segment}', group: 'Master Data Sync', isCallback: true, sourceKey: 'Get_users', idField: 'userId' },

    { key: 'Get_printer_details', label: 'Printer Details', method: 'GET', path: 'api/vikraya_app/Get_printer_details/{segment}', group: 'Master Data Sync' },
    { key: 'callback_printer_details', label: 'Callback Printer Details', method: 'POST', path: 'api/vikraya_app/callback_printer_details/{segment}', group: 'Master Data Sync', isCallback: true, sourceKey: 'Get_printer_details', idField: 'printer_id' },

    { key: 'Get_notifications', label: 'Notifications', method: 'GET', path: 'api/vikraya_app/Get_notifications/{segment}', group: 'Master Data Sync' },
    { key: 'callback_notifications', label: 'Callback Notifications', method: 'POST', path: 'api/vikraya_app/callback_notifications/{segment}', group: 'Master Data Sync', isCallback: true, sourceKey: 'Get_notifications', idField: 'notification_id' },

    { key: 'get_credit_report', label: 'Credit Report', method: 'GET', path: 'api/vikraya_app/get_credit_report/{segment}', group: 'Reports & Helpers' },
    { key: 'get_lastbill_details', label: 'Last Bill Details', method: 'GET', path: 'api/vikraya_app/get_lastbill_details/{segment}/{extra}', group: 'Reports & Helpers', extraParam: { key: 'cust_id', label: 'Customer ID' } },
    { key: 'get_items_report', label: 'Items Report', method: 'GET', path: 'api/vikraya_app/get_items_report/{segment}/{extra}', group: 'Reports & Helpers', extraParam: { key: 'user_id', label: 'User ID' } },
    { key: 'special_product_cust', label: 'Special Product Pricing', method: 'GET', path: 'api/vikraya_app/special_product_cust/{segment}/{extra}', group: 'Reports & Helpers', extraParam: { key: 'cust_id', label: 'Customer ID' } },
    { key: 'get_customer_data', label: 'Customer Data', method: 'GET', path: 'api/vikraya_app/get_customer_data/{segment}', group: 'Reports & Helpers' },
    { key: 'upload_image', label: 'Upload Image', method: 'POST', path: 'api/vikraya_app/upload_image', group: 'Reports & Helpers', noBranch: true, isUpload: true },
    { key: 'get_ref_inforamtion', label: 'Ref Information', method: 'POST', path: 'api/vikraya_app/get_ref_inforamtion', group: 'Reports & Helpers', noBranch: true },
    { key: 'get_payment_callback', label: 'Payment Callback', method: 'POST', path: 'api/vikraya_app/get_payment_callback', group: 'Reports & Helpers', noBranch: true }
];

const GROUP_STYLES = {
    'Login & Bill Sync': { color: '#1d4ed8', bg: '#eff6ff', accent: '#3b82f6', soft: 'rgba(59, 130, 246, 0.10)', icon: 'pi pi-lock' },
    'Master Data Sync': { color: '#0f766e', bg: '#f0fdfa', accent: '#14b8a6', soft: 'rgba(20, 184, 166, 0.10)', icon: 'pi pi-database' },
    'Reports & Helpers': { color: '#b45309', bg: '#fffbeb', accent: '#f59e0b', soft: 'rgba(245, 158, 11, 0.10)', icon: 'pi pi-chart-bar' }
};
const DEFAULT_GROUP_STYLE = { color: '#374151', bg: '#f9fafb', accent: '#9ca3af', soft: 'rgba(156, 163, 175, 0.12)', icon: 'pi pi-bookmark' };
const groupStyle = (group) => GROUP_STYLES[group] || DEFAULT_GROUP_STYLE;

const LOGIN_TEMPLATE = JSON.stringify({ username: '', password: '' }, null, 2);
const COUNTERDATA_TEMPLATE = JSON.stringify(
    {
        Order: [
            {
                bill_id: '',
                in_no: '',
                invoice_no: '',
                invoice_date: '', // Y-m-d
                invoice_time: '', // h:i A  e.g. 07:45 PM
                cust_name: '',
                cust_phone: '',
                total_amount: '',
                paid_amt: '',
                mop: 'CASH',
                final_mop: 'CASH',
                branch: '',
                user_id: '',
                order_type: 'COUNTER',
                status: 'synched',
                counter_items: [],
                counter_payments: []
            }
        ]
    },
    null,
    2
);
const CALLBACK_TEMPLATE = JSON.stringify({ data: [] }, null, 2);
const REF_INFO_TEMPLATE = JSON.stringify({}, null, 2);
const PAYMENT_CALLBACK_TEMPLATE = JSON.stringify({}, null, 2);

/** Hints for endpoints whose exact request/response shape is not hardcoded above — the
 *  JSON body is fully editable, so this just points at what to try first. */
const NO_BODY_HINTS = {
    get_billdetails: 'Select a branch and click Run to read back the highest bill / item / payment ids for that branch. No request body is used.',
    send_sms: "Select a branch and click Run to trigger today's closing-day-sales SMS for that branch. No request body is used.",
    sales_tracking_records: "Builds today's closing-day-sales message from this branch's own bill and payment tables and sends it. No request body is used.",
    get_credit_report: 'Select a branch and click Run to pull its credit report.',
    get_customer_data: 'Select a branch and click Run to pull its customer list.'
};

const displayBaseUrl = () => (process.env.NODE_ENV === 'development' ? 'http://localhost/Counter-Backend' : getSalesReactBaseUrl());
const trimUrl = (url) => String(url || '').replace(/\/+$/, '');
const rawResultText = (data) => (typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data));

const describeError = (err) => {
    if (err.response) return err.message;
    return `${err.message}\n\nNo HTTP response came back. Either this client's deployment is down, or the browser blocked the call: this page's origin must be listed in that deployment's Config/Cors.php allowedOrigins.`;
};

// Get_application_setting-style endpoints return their rows under `Details` or `data`.
const extractIds = (raw, idField) => {
    const rows = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : Array.isArray(raw?.Details) ? raw.Details : [];
    return rows.map((r) => r?.[idField]).filter((v) => v !== undefined && v !== null);
};

/** Compact status / method chip used across the list and detail pane. */
const Pill = ({ tone, children }) => <span className={`gb-pill gb-pill-${tone}`}>{children}</span>;

/** Result panel: status strip + JSON viewer, shared by GET steps and POST steps. */
const ResultPanel = ({ result, maxHeight = 260 }) => {
    const [copied, setCopied] = useState(false);
    if (!result) return null;
    const ok = result.status === 'success';
    const text = rawResultText(result.data);
    const copy = () => {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        });
    };
    return (
        <div className={`gb-res ${ok ? 'is-ok' : 'is-err'}`}>
            <div className="gb-res-bar">
                <span className="gb-res-dot" />
                <span className="gb-res-code">{ok ? `${result.statusCode} OK` : `Error ${result.statusCode || ''}`}</span>
                <span className="gb-res-meta">
                    <i className="pi pi-clock" /> {result.duration} ms
                </span>
                {result.testedAt && (
                    <span className="gb-res-meta gb-hide-sm">
                        <i className="pi pi-calendar" /> {result.testedAt}
                    </span>
                )}
                <span className="gb-res-spacer" />
                <button type="button" className="gb-copy" onClick={copy}>
                    <i className={copied ? 'pi pi-check' : 'pi pi-copy'} /> {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <pre className="gb-res-body" style={{ maxHeight }}>
                {text}
            </pre>
        </div>
    );
};

const StepHint = ({ icon = 'pi pi-info-circle', children }) => (
    <div className="gb-hint">
        <i className={icon} />
        <span>{children}</span>
    </div>
);
const StepLoading = ({ children }) => (
    <div className="gb-hint gb-hint-loading">
        <i className="pi pi-spin pi-spinner" />
        <span>{children}</span>
    </div>
);

/** Minimal, uniform block for a GET/no-body step: request URL + run button, status/JSON. */
const StepBlock = ({ title, action, method, url, loading, loadingText, result, emptyHint, maxHeight = 260 }) => (
    <div className="gb-step">
        {title && <div className="gb-step-title">{title}</div>}
        <div className="gb-reqbar">
            <span className={`gb-reqbar-method gb-method-${method.toLowerCase()}`}>{method}</span>
            <span className="gb-reqbar-url">{url}</span>
            <span className="gb-reqbar-action">{action}</span>
        </div>
        {loading && <StepLoading>{loadingText || 'Loading…'}</StepLoading>}
        {result ? <ResultPanel result={result} maxHeight={maxHeight} /> : !loading && <StepHint>{emptyHint}</StepHint>}
    </div>
);

/** Step for POST endpoints that need an editable JSON request body. */
const PostBodyStep = ({ title, url, bodyText, onBodyChange, onRun, loading, loadingText, result, sendLabel = 'Send', maxHeight = 260 }) => (
    <div className="gb-step">
        {title && <div className="gb-step-title">{title}</div>}
        <div className="gb-reqbar">
            <span className="gb-reqbar-method gb-method-post">POST</span>
            <span className="gb-reqbar-url">{url}</span>
            <span className="gb-reqbar-action">
                <Button label={sendLabel} icon="pi pi-send" className="p-button-sm" onClick={onRun} loading={loading} />
            </span>
        </div>
        <div className="gb-editor">
            <div className="gb-editor-bar">
                <i className="pi pi-code" />
                <span>Request body</span>
                <span className="gb-res-spacer" />
                <span className="gb-editor-tag">JSON</span>
            </div>
            <textarea className="gb-json-edit" value={bodyText} onChange={(e) => onBodyChange(e.target.value)} spellCheck={false} />
        </div>
        {loading && <StepLoading>{loadingText || 'Sending…'}</StepLoading>}
        {result && <ResultPanel result={result} maxHeight={maxHeight} />}
    </div>
);

/** Step for a GET endpoint that needs a second path segment (cust_id / user_id) typed in first. */
const ExtraParamStep = ({ paramLabel, paramValue, onParamChange, method, url, action, loading, loadingText, result, emptyHint, maxHeight = 260 }) => (
    <div className="gb-step">
        <div className="gb-field" style={{ marginBottom: 12, maxWidth: 320 }}>
            <label className="gb-field-label">
                <i className="pi pi-hashtag" /> {paramLabel}
            </label>
            <InputText value={paramValue} onChange={(e) => onParamChange(e.target.value)} placeholder={`Enter ${paramLabel.toLowerCase()}`} className="w-full" />
        </div>
        <div className="gb-reqbar">
            <span className={`gb-reqbar-method gb-method-${method.toLowerCase()}`}>{method}</span>
            <span className="gb-reqbar-url">{url}</span>
            <span className="gb-reqbar-action">{action}</span>
        </div>
        {loading && <StepLoading>{loadingText || 'Loading…'}</StepLoading>}
        {result ? <ResultPanel result={result} maxHeight={maxHeight} /> : !loading && <StepHint>{emptyHint}</StepHint>}
    </div>
);

/** Step for Upload Image — a real file field (multipart), not a JSON body. */
const UploadStep = ({ url, file, onFileChange, extraFields, onExtraFieldsChange, onRun, loading, result }) => (
    <div className="gb-step">
        <div className="gb-reqbar">
            <span className="gb-reqbar-method gb-method-post">POST</span>
            <span className="gb-reqbar-url">{url}</span>
            <span className="gb-reqbar-action">
                <Button label="Upload" icon="pi pi-upload" className="p-button-sm" onClick={onRun} loading={loading} disabled={!file} />
            </span>
        </div>
        <div className="gb-field" style={{ marginTop: 12, maxWidth: 420 }}>
            <label className="gb-field-label">
                <i className="pi pi-image" /> Image file
            </label>
            <input type="file" accept="image/*" onChange={(e) => onFileChange(e.target.files?.[0] || null)} />
        </div>
        <div className="gb-editor" style={{ marginTop: 12 }}>
            <div className="gb-editor-bar">
                <i className="pi pi-code" />
                <span>Extra form fields (JSON object — merged in alongside the file)</span>
                <span className="gb-res-spacer" />
                <span className="gb-editor-tag">JSON</span>
            </div>
            <textarea className="gb-json-edit" value={extraFields} onChange={(e) => onExtraFieldsChange(e.target.value)} spellCheck={false} />
        </div>
        {loading && <StepLoading>Uploading…</StepLoading>}
        {result && <ResultPanel result={result} />}
    </div>
);

export default function VikrayaEndpoints() {
    const toast = useRef(null);

    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [clientBaseUrl, setClientBaseUrl] = useState('');
    const branchCacheRef = useRef(new Map());

    const [results, setResults] = useState({});
    const [runningKeys, setRunningKeys] = useState(new Set());

    const [postBody, setPostBody] = useState({});
    const [postResults, setPostResults] = useState({});
    const [postLoading, setPostLoading] = useState({});

    const [extraParams, setExtraParams] = useState({});

    const [uploadFile, setUploadFile] = useState(null);
    const [uploadExtraFields, setUploadExtraFields] = useState('{}');

    const [selectedEndpoint, setSelectedEndpoint] = useState(null);
    const [search, setSearch] = useState('');
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    const toggleGroup = (group) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(group)) next.delete(group);
            else next.add(group);
            return next;
        });
    };

    useEffect(() => {
        fetchClients();
    }, []);

    // Auto-fill a callback body with pending IDs from its matching GET endpoint, unless the user already edited it.
    useEffect(() => {
        if (!selectedEndpoint?.isCallback) return;
        const r = results[selectedEndpoint.sourceKey];
        if (r?.status !== 'success') return;
        const key = selectedEndpoint.key;
        setPostBody((prev) => {
            if (prev[key] !== undefined) return prev;
            const ids = extractIds(r.data, selectedEndpoint.idField);
            return { ...prev, [key]: JSON.stringify({ data: ids }, null, 2) };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results, selectedEndpoint]);

    const fetchClients = async () => {
        try {
            const data = await getLiveDataClients();
            setClients(data.map((c) => ({ label: c.client_name, value: c.client_id, base_url: c.base_url || '' })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load clients' });
        }
    };

    const fetchBranches = async (clientId) => {
        const cached = branchCacheRef.current.get(clientId);
        if (cached) {
            setBranches(cached);
            return;
        }
        setBranchesLoading(true);
        try {
            const data = await getLiveDataBranches(clientId);
            const options = data.map((b) => ({ label: b.branch_name, value: b.branch_id }));
            branchCacheRef.current.set(clientId, options);
            setBranches(options);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load branches' });
        } finally {
            setBranchesLoading(false);
        }
    };

    const currentSegment = () => {
        const branchObj = branches.find((b) => b.value === selectedBranch);
        return branchObj ? branchObj.label : selectedBranch || '';
    };

    const baseUrl = trimUrl(clientBaseUrl);

    const resolveUrl = (endpoint, extraValue) => {
        let path = endpoint.path.replace('{segment}', currentSegment());
        if (endpoint.extraParam) path = path.replace('{extra}', extraValue || `{${endpoint.extraParam.key}}`);
        return `${baseUrl || displayBaseUrl()}/${path}`;
    };

    const runEndpoint = async (endpoint) => {
        const needsSegment = endpoint.path.includes('{segment}') && !endpoint.noBranch;
        if (needsSegment && !selectedBranch) {
            toast.current.show({ severity: 'warn', summary: 'Branch Required', detail: 'Please select a client and branch first' });
            return;
        }
        setRunningKeys((prev) => new Set(prev).add(endpoint.key));
        const t0 = Date.now();
        try {
            const data = await ENDPOINT_HANDLERS[endpoint.key](currentSegment(), baseUrl);
            setResults((prev) => ({ ...prev, [endpoint.key]: { status: 'success', statusCode: 200, data, duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() } }));
        } catch (err) {
            setResults((prev) => ({
                ...prev,
                [endpoint.key]: { status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || describeError(err), duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() }
            }));
        } finally {
            setRunningKeys((prev) => {
                const n = new Set(prev);
                n.delete(endpoint.key);
                return n;
            });
        }
    };

    const runExtraParamEndpoint = async (endpoint) => {
        if (!selectedBranch) {
            toast.current.show({ severity: 'warn', summary: 'Branch Required', detail: 'Please select a client and branch first' });
            return;
        }
        const value = (extraParams[endpoint.key] || '').trim();
        if (!value) {
            toast.current.show({ severity: 'warn', summary: `${endpoint.extraParam.label} Required`, detail: `Enter a ${endpoint.extraParam.label.toLowerCase()} first` });
            return;
        }
        setRunningKeys((prev) => new Set(prev).add(endpoint.key));
        const t0 = Date.now();
        try {
            const data = await EXTRA_PARAM_HANDLERS[endpoint.key](currentSegment(), value, baseUrl);
            setResults((prev) => ({ ...prev, [endpoint.key]: { status: 'success', statusCode: 200, data, duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() } }));
        } catch (err) {
            setResults((prev) => ({
                ...prev,
                [endpoint.key]: { status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || describeError(err), duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() }
            }));
        } finally {
            setRunningKeys((prev) => {
                const n = new Set(prev);
                n.delete(endpoint.key);
                return n;
            });
        }
    };

    const runPostBody = async (endpoint, { requiresBranch = true, buildRequest, defaultBody }) => {
        if (requiresBranch && !selectedBranch) {
            toast.current.show({ severity: 'warn', summary: 'Branch Required', detail: 'Please select a client and branch first' });
            return;
        }
        const key = endpoint.key;
        const bodyText = postBody[key] ?? defaultBody;
        let parsed;
        try {
            parsed = bodyText.trim() ? JSON.parse(bodyText) : {};
        } catch (e) {
            setPostResults((prev) => ({ ...prev, [key]: { status: 'error', statusCode: 0, data: `Invalid JSON: ${e.message}`, duration: 0, testedAt: new Date().toLocaleTimeString() } }));
            return;
        }
        setPostLoading((prev) => ({ ...prev, [key]: true }));
        const t0 = Date.now();
        try {
            const data = await buildRequest(currentSegment(), parsed, baseUrl);
            setPostResults((prev) => ({ ...prev, [key]: { status: 'success', statusCode: 200, data, duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() } }));
        } catch (err) {
            setPostResults((prev) => ({
                ...prev,
                [key]: { status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || describeError(err), duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() }
            }));
        } finally {
            setPostLoading((prev) => ({ ...prev, [key]: false }));
        }
    };

    const runUpload = async (endpoint) => {
        if (!uploadFile) return;
        let extra = {};
        try {
            extra = uploadExtraFields.trim() ? JSON.parse(uploadExtraFields) : {};
        } catch (e) {
            setPostResults((prev) => ({ ...prev, [endpoint.key]: { status: 'error', statusCode: 0, data: `Invalid JSON: ${e.message}`, duration: 0, testedAt: new Date().toLocaleTimeString() } }));
            return;
        }
        setPostLoading((prev) => ({ ...prev, [endpoint.key]: true }));
        const t0 = Date.now();
        try {
            const formData = new FormData();
            formData.append('image', uploadFile);
            Object.entries(extra).forEach(([k, v]) => formData.append(k, v));
            const data = await uploadVikrayaImage(formData, baseUrl);
            setPostResults((prev) => ({ ...prev, [endpoint.key]: { status: 'success', statusCode: 200, data, duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() } }));
        } catch (err) {
            setPostResults((prev) => ({
                ...prev,
                [endpoint.key]: { status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || describeError(err), duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() }
            }));
        } finally {
            setPostLoading((prev) => ({ ...prev, [endpoint.key]: false }));
        }
    };

    const selectEndpoint = (endpoint) => setSelectedEndpoint(endpoint);

    const statusInfo = (endpoint) => {
        if (runningKeys.has(endpoint.key) || postLoading[endpoint.key]) return { text: 'Running…', severity: 'info' };
        const r = postResults[endpoint.key] || results[endpoint.key];
        if (!r) return { text: 'Not Tested', severity: 'secondary' };
        return r.status === 'success' ? { text: `${r.statusCode} OK`, severity: 'success' } : { text: `Error ${r.statusCode || ''}`, severity: 'danger' };
    };

    const successCount = ENDPOINTS.filter((e) => statusInfo(e).severity === 'success').length;
    const failCount = ENDPOINTS.filter((e) => statusInfo(e).severity === 'danger').length;
    const notTested = ENDPOINTS.length - successCount - failCount;
    const coverage = Math.round(((successCount + failCount) / ENDPOINTS.length) * 100);

    const groupedEndpoints = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = q ? ENDPOINTS.filter((e) => e.label.toLowerCase().includes(q)) : ENDPOINTS;
        const groups = {};
        filtered.forEach((e) => {
            groups[e.group] = groups[e.group] || [];
            groups[e.group].push(e);
        });
        return groups;
    }, [search]);

    const renderDetailBody = () => {
        if (!selectedEndpoint) return null;
        const ep = selectedEndpoint;

        if (ep.key === 'login_authenticate') {
            return (
                <PostBodyStep
                    url={resolveUrl(ep)}
                    bodyText={postBody[ep.key] ?? LOGIN_TEMPLATE}
                    onBodyChange={(v) => setPostBody((prev) => ({ ...prev, [ep.key]: v }))}
                    onRun={() => runPostBody(ep, { requiresBranch: false, buildRequest: (_, parsed, url) => vikrayaLoginAuthenticate(parsed, url), defaultBody: LOGIN_TEMPLATE })}
                    loading={!!postLoading[ep.key]}
                    loadingText="Authenticating…"
                    result={postResults[ep.key]}
                    sendLabel="Login"
                />
            );
        }

        if (ep.key === 'counterdata_synching') {
            return (
                <PostBodyStep
                    url={resolveUrl(ep)}
                    bodyText={postBody[ep.key] ?? COUNTERDATA_TEMPLATE}
                    onBodyChange={(v) => setPostBody((prev) => ({ ...prev, [ep.key]: v }))}
                    onRun={() => runPostBody(ep, { requiresBranch: true, buildRequest: (segment, parsed, url) => syncVikrayaCounterData(segment, parsed, url), defaultBody: COUNTERDATA_TEMPLATE })}
                    loading={!!postLoading[ep.key]}
                    loadingText="Syncing counter data…"
                    result={postResults[ep.key]}
                    sendLabel="Sync"
                    maxHeight={320}
                />
            );
        }

        if (ep.key === 'upload_image') {
            return (
                <UploadStep
                    url={resolveUrl(ep)}
                    file={uploadFile}
                    onFileChange={setUploadFile}
                    extraFields={uploadExtraFields}
                    onExtraFieldsChange={setUploadExtraFields}
                    onRun={() => runUpload(ep)}
                    loading={!!postLoading[ep.key]}
                    result={postResults[ep.key]}
                />
            );
        }

        if (ep.key === 'get_ref_inforamtion') {
            return (
                <PostBodyStep
                    title="Exact request shape isn't hardcoded here — edit the body freely and inspect the response."
                    url={resolveUrl(ep)}
                    bodyText={postBody[ep.key] ?? REF_INFO_TEMPLATE}
                    onBodyChange={(v) => setPostBody((prev) => ({ ...prev, [ep.key]: v }))}
                    onRun={() => runPostBody(ep, { requiresBranch: false, buildRequest: (_, parsed, url) => getVikrayaRefInformation(parsed, url), defaultBody: REF_INFO_TEMPLATE })}
                    loading={!!postLoading[ep.key]}
                    loadingText="Sending…"
                    result={postResults[ep.key]}
                />
            );
        }

        if (ep.key === 'get_payment_callback') {
            return (
                <PostBodyStep
                    title="Exact request shape isn't hardcoded here — edit the body freely and inspect the response."
                    url={resolveUrl(ep)}
                    bodyText={postBody[ep.key] ?? PAYMENT_CALLBACK_TEMPLATE}
                    onBodyChange={(v) => setPostBody((prev) => ({ ...prev, [ep.key]: v }))}
                    onRun={() => runPostBody(ep, { requiresBranch: false, buildRequest: (_, parsed, url) => getVikrayaPaymentCallback(parsed, url), defaultBody: PAYMENT_CALLBACK_TEMPLATE })}
                    loading={!!postLoading[ep.key]}
                    loadingText="Sending…"
                    result={postResults[ep.key]}
                />
            );
        }

        if (ep.isCallback) {
            const sourceLabel = ENDPOINTS.find((e) => e.key === ep.sourceKey)?.label;
            return (
                <PostBodyStep
                    title={results[ep.sourceKey] ? `IDs pre-filled from the last ${sourceLabel} fetch — edit if needed.` : `Run ${sourceLabel} first to pre-fill the pending IDs, or paste them below.`}
                    url={resolveUrl(ep)}
                    bodyText={postBody[ep.key] ?? CALLBACK_TEMPLATE}
                    onBodyChange={(v) => setPostBody((prev) => ({ ...prev, [ep.key]: v }))}
                    onRun={() => runPostBody(ep, { requiresBranch: true, buildRequest: (segment, parsed, url) => CALLBACK_HANDLERS[ep.key](segment, parsed, url), defaultBody: CALLBACK_TEMPLATE })}
                    loading={!!postLoading[ep.key]}
                    loadingText="Acknowledging…"
                    result={postResults[ep.key]}
                    sendLabel="Acknowledge"
                />
            );
        }

        if (ep.extraParam) {
            return (
                <ExtraParamStep
                    paramLabel={ep.extraParam.label}
                    paramValue={extraParams[ep.key] || ''}
                    onParamChange={(v) => setExtraParams((prev) => ({ ...prev, [ep.key]: v }))}
                    method={ep.method}
                    url={resolveUrl(ep, extraParams[ep.key])}
                    action={<Button label="Run" icon="pi pi-play" className="p-button-sm" onClick={() => runExtraParamEndpoint(ep)} loading={runningKeys.has(ep.key)} />}
                    result={results[ep.key]}
                    emptyHint={`Select a branch, enter a ${ep.extraParam.label.toLowerCase()}, and click Run.`}
                    maxHeight={320}
                />
            );
        }

        // Remaining endpoints take only the branch segment — select a branch, run, inspect the response.
        return (
            <StepBlock
                method={ep.method}
                url={resolveUrl(ep)}
                action={<Button label="Run" icon="pi pi-play" className="p-button-sm" onClick={() => runEndpoint(ep)} loading={runningKeys.has(ep.key)} />}
                result={results[ep.key]}
                emptyHint={NO_BODY_HINTS[ep.key] || `Select a branch and click Run to fetch pending ${ep.label.toLowerCase()} rows.`}
                maxHeight={320}
            />
        );
    };

    const detailStyle = selectedEndpoint ? groupStyle(selectedEndpoint.group) : DEFAULT_GROUP_STYLE;

    return (
        <div className="vikraya-page">
            <Toast ref={toast} />
            <style>{`
                .vikraya-page { padding: 1.25rem; background: #f4f5fa; min-height: 100%; }

                /* ── Hero ───────────────────────────────────────────── */
                .vikraya-page .gb-hero { position: relative; overflow: hidden; border-radius: 18px; padding: 22px 26px; margin-bottom: 16px;
                    background: linear-gradient(135deg, #92400e 0%, #b45309 45%, #f59e0b 100%);
                    box-shadow: 0 12px 30px -12px rgba(180, 83, 9, 0.45); display: flex; gap: 24px; align-items: center; flex-wrap: wrap; }
                .vikraya-page .gb-hero::after { content: ''; position: absolute; right: -70px; top: -110px; width: 320px; height: 320px; border-radius: 50%;
                    background: radial-gradient(circle, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 68%); pointer-events: none; }
                .vikraya-page .gb-hero-main { display: flex; align-items: center; gap: 16px; flex: 1; min-width: 280px; position: relative; z-index: 1; }
                .vikraya-page .gb-hero-icon { width: 52px; height: 52px; flex-shrink: 0; border-radius: 14px; display: flex; align-items: center; justify-content: center;
                    background: rgba(255,255,255,0.16); border: 1px solid rgba(255,255,255,0.25); backdrop-filter: blur(6px); }
                .vikraya-page .gb-hero-icon i { font-size: 1.5rem; color: #fff; }
                .vikraya-page .gb-hero-title { margin: 0; font-size: 1.4rem; font-weight: 800; color: #fff; letter-spacing: -0.02em; line-height: 1.2; }
                .vikraya-page .gb-hero-sub { margin: 5px 0 0; font-size: 0.82rem; color: rgba(255,255,255,0.78); max-width: 640px; line-height: 1.45; }

                .vikraya-page .gb-stats { display: flex; gap: 10px; flex-wrap: wrap; position: relative; z-index: 1; }
                .vikraya-page .gb-stat { min-width: 96px; padding: 10px 14px; border-radius: 12px; background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.18); backdrop-filter: blur(8px); transition: transform .18s ease, background-color .18s ease; }
                .vikraya-page .gb-stat:hover { transform: translateY(-2px); background: rgba(255,255,255,0.18); }
                .vikraya-page .gb-stat-value { font-size: 1.4rem; font-weight: 800; color: #fff; line-height: 1.1; display: flex; align-items: center; gap: 7px; }
                .vikraya-page .gb-stat-value i { font-size: 0.8rem; }
                .vikraya-page .gb-stat-label { font-size: 0.63rem; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: rgba(255,255,255,0.7); margin-top: 3px; }
                .vikraya-page .gb-stat.ok .gb-stat-value i { color: #6ee7b7; }
                .vikraya-page .gb-stat.err .gb-stat-value i { color: #fda4af; }
                .vikraya-page .gb-stat.idle .gb-stat-value i { color: #cbd5e1; }
                .vikraya-page .gb-stat.all .gb-stat-value i { color: #fde68a; }
                .vikraya-page .gb-progress { height: 4px; border-radius: 4px; background: rgba(255,255,255,0.2); margin-top: 8px; overflow: hidden; }
                .vikraya-page .gb-progress span { display: block; height: 100%; border-radius: 4px; background: linear-gradient(90deg, #fcd34d, #6ee7b7); transition: width .4s ease; }

                /* ── Toolbar ────────────────────────────────────────── */
                .vikraya-page .gb-toolbar { background: #fff; border: 1px solid #e8eaf2; border-radius: 16px; padding: 14px 18px; margin-bottom: 16px;
                    box-shadow: 0 2px 10px -4px rgba(15, 23, 42, 0.10); display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap; }
                .vikraya-page .gb-field { display: flex; flex-direction: column; flex: 1; min-width: 210px; }
                .vikraya-page .gb-field-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #b45309; margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
                .vikraya-page .gb-field-label .gb-muted { color: #a1a1b5; font-weight: 600; letter-spacing: 0.04em; text-transform: none; }
                .vikraya-page .gb-field .p-dropdown { border-radius: 10px; border-color: #e2e5ef; }
                .vikraya-page .gb-field .p-dropdown:not(.p-disabled):hover { border-color: #fcd34d; }
                .vikraya-page .gb-field .p-dropdown:not(.p-disabled).p-focus { border-color: #b45309; box-shadow: 0 0 0 3px rgba(180, 83, 9, 0.14); }
                .vikraya-page .gb-segment { display: inline-flex; align-items: center; gap: 7px; padding: 8px 13px; border-radius: 10px; background: #fffbeb;
                    border: 1px dashed #fcd34d; color: #92400e; font-size: 0.78rem; font-weight: 600; font-family: ui-monospace, Menlo, Consolas, monospace; white-space: nowrap; }
                .vikraya-page .gb-segment i { font-size: 0.75rem; }
                .vikraya-page .gb-clear.p-button { border-radius: 10px; background: #fff; border: 1px solid #e2e5ef; color: #64748b; font-weight: 600; }
                .vikraya-page .gb-clear.p-button:enabled:hover { background: #f8fafc; border-color: #cbd5e1; color: #475569; }

                /* ── Split view ─────────────────────────────────────── */
                .vikraya-page .gb-split { display: flex; gap: 16px; height: 70vh; min-height: 500px; }
                .vikraya-page .gb-sidebar, .vikraya-page .gb-detail { background: #fff; border: 1px solid #e8eaf2; border-radius: 16px;
                    box-shadow: 0 2px 10px -4px rgba(15, 23, 42, 0.10); overflow-y: auto; }
                .vikraya-page .gb-sidebar { width: 340px; flex-shrink: 0; }
                .vikraya-page .gb-detail { flex: 1; display: flex; flex-direction: column; }
                .vikraya-page .gb-sidebar::-webkit-scrollbar, .vikraya-page .gb-detail::-webkit-scrollbar, .vikraya-page .gb-res-body::-webkit-scrollbar { width: 8px; height: 8px; }
                .vikraya-page .gb-sidebar::-webkit-scrollbar-thumb, .vikraya-page .gb-detail::-webkit-scrollbar-thumb { background: #dfe3ec; border-radius: 8px; }
                .vikraya-page .gb-sidebar::-webkit-scrollbar-thumb:hover, .vikraya-page .gb-detail::-webkit-scrollbar-thumb:hover { background: #c7cddb; }
                .vikraya-page .gb-res-body::-webkit-scrollbar-thumb { background: #334155; border-radius: 8px; }

                /* Search */
                .vikraya-page .gb-search { position: sticky; top: 0; z-index: 3; background: #fff; padding: 12px; border-bottom: 1px solid #eef0f6; }
                .vikraya-page .gb-search .p-input-icon-left { width: 100%; }
                .vikraya-page .gb-search .p-inputtext { width: 100%; border-radius: 10px; background: #f7f8fc; border-color: #e8eaf2; font-size: 0.85rem; padding-left: 2.4rem; }
                .vikraya-page .gb-search .p-inputtext:enabled:focus { background: #fff; border-color: #b45309; box-shadow: 0 0 0 3px rgba(180, 83, 9, 0.12); }
                .vikraya-page .gb-search i { color: #a5abbd; }

                /* Group headers */
                .vikraya-page .gb-group-header { padding: 9px 14px; font-weight: 800; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.09em;
                    position: sticky; top: 61px; z-index: 2; display: flex; align-items: center; gap: 8px; user-select: none; cursor: pointer; transition: filter .15s ease; }
                .vikraya-page .gb-group-header:hover { filter: brightness(0.97); }
                .vikraya-page .gb-group-count { font-weight: 700; font-size: 0.68rem; padding: 1px 8px; border-radius: 999px; background: rgba(255,255,255,0.72); }

                /* List items */
                .vikraya-page .gb-item { cursor: pointer; padding: 11px 14px 11px 15px; border-left: 3px solid transparent; border-bottom: 1px solid #f1f3f8;
                    transition: background-color .15s ease, border-color .15s ease, padding-left .15s ease; }
                .vikraya-page .gb-item:hover { background: #f8f9fd; padding-left: 18px; }
                .vikraya-page .gb-item.selected { background: linear-gradient(90deg, #fffbeb 0%, #fffdf7 100%); }
                .vikraya-page .gb-item.selected .gb-item-title { color: #92400e; }
                .vikraya-page .gb-item-row { display: flex; align-items: center; gap: 8px; }
                .vikraya-page .gb-item-title { font-weight: 600; font-size: 0.85rem; color: #1e293b; flex: 1; line-height: 1.3; }
                .vikraya-page .gb-item-meta { font-size: 0.7rem; color: #a5abbd; }
                .vikraya-page .gb-item-foot { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
                .vikraya-page .gb-empty-search { padding: 26px 16px; text-align: center; color: #a5abbd; font-size: 0.83rem; }

                /* Pills */
                .vikraya-page .gb-pill { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 999px;
                    font-size: 0.63rem; font-weight: 700; letter-spacing: 0.03em; line-height: 1.6; border: 1px solid transparent; white-space: nowrap; }
                .vikraya-page .gb-pill-get { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
                .vikraya-page .gb-pill-post { background: #fff7ed; color: #b45309; border-color: #fed7aa; }
                .vikraya-page .gb-pill-success { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
                .vikraya-page .gb-pill-danger { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
                .vikraya-page .gb-pill-secondary { background: #f4f5f9; color: #64748b; border-color: #e2e5ef; }
                .vikraya-page .gb-pill-info { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }

                /* Detail header */
                .vikraya-page .gb-detail-header { padding: 18px 24px; border-bottom: 1px solid #eef0f6; position: sticky; top: 0; z-index: 2; backdrop-filter: blur(6px); }
                .vikraya-page .gb-crumb { display: flex; align-items: center; gap: 8px; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 8px; }
                .vikraya-page .gb-detail-title { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
                .vikraya-page .gb-detail-title h3 { margin: 0; font-size: 1.28rem; font-weight: 800; color: #1e293b; letter-spacing: -0.02em; }
                .vikraya-page .gb-detail-path { margin-top: 8px; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.74rem; color: #94a3b8; word-break: break-all; }
                .vikraya-page .gb-detail-body { padding: 18px 24px 24px; }

                /* Steps */
                .vikraya-page .gb-step { padding: 2px 0; }
                .vikraya-page .gb-step-title { font-weight: 600; font-size: 0.82rem; color: #475569; margin-bottom: 10px; padding: 9px 12px;
                    background: #f8f9fd; border: 1px solid #eef0f6; border-left: 3px solid #fcd34d; border-radius: 8px; }
                .vikraya-page .gb-hint { display: flex; align-items: flex-start; gap: 9px; margin-top: 12px; padding: 12px 14px; border-radius: 10px;
                    background: #f8f9fd; border: 1px dashed #e2e5ef; color: #7c8598; font-size: 0.8rem; line-height: 1.5; }
                .vikraya-page .gb-hint i { color: #fbbf24; margin-top: 2px; }
                .vikraya-page .gb-hint-loading { background: #fffbeb; border-color: #fde68a; color: #92400e; }

                /* Request bar */
                .vikraya-page .gb-reqbar { display: flex; align-items: stretch; border: 1px solid #e2e5ef; border-radius: 12px; overflow: hidden;
                    background: #fbfbfe; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05); }
                .vikraya-page .gb-reqbar-method { display: flex; align-items: center; padding: 0 15px; font-weight: 800; font-size: 0.74rem;
                    font-family: ui-monospace, Menlo, Consolas, monospace; letter-spacing: 0.06em; border-right: 1px solid #e2e5ef; white-space: nowrap; }
                .vikraya-page .gb-method-get { color: #047857; background: #ecfdf5; }
                .vikraya-page .gb-method-post { color: #b45309; background: #fff7ed; }
                .vikraya-page .gb-reqbar-url { flex: 1; display: flex; align-items: center; padding: 11px 14px; font-family: ui-monospace, Menlo, Consolas, monospace;
                    font-size: 0.78rem; color: #475569; word-break: break-all; min-width: 0; }
                .vikraya-page .gb-reqbar-action { display: flex; align-items: stretch; }
                .vikraya-page .gb-reqbar-action .p-button { height: 100%; border-radius: 0 !important; padding-left: 18px; padding-right: 18px; font-weight: 600;
                    background: linear-gradient(135deg, #b45309, #f59e0b) !important; border: none !important; box-shadow: none !important; }
                .vikraya-page .gb-reqbar-action .p-button:enabled:hover { background: linear-gradient(135deg, #92400e, #b45309) !important; }
                .vikraya-page .gb-reqbar-action .p-button:disabled { background: #cbd5e1 !important; opacity: 1; }

                /* JSON editor */
                .vikraya-page .gb-editor { margin-top: 12px; border-radius: 12px; overflow: hidden; border: 1px solid #1e293b; }
                .vikraya-page .gb-editor-bar { display: flex; align-items: center; gap: 8px; padding: 8px 13px; background: #111827;
                    color: #94a3b8; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.04em; }
                .vikraya-page .gb-editor-tag { padding: 1px 8px; border-radius: 999px; background: rgba(148, 163, 184, 0.16); color: #cbd5e1; font-size: 0.62rem; font-weight: 700; }
                .vikraya-page .gb-json-edit { width: 100%; min-height: 150px; resize: vertical; box-sizing: border-box; display: block; border: none; outline: none;
                    background: #0f172a; color: #e2e8f0; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.78rem; line-height: 1.6; padding: 13px 15px; }
                .vikraya-page .gb-json-edit::selection { background: rgba(180, 83, 9, 0.45); }

                /* Response viewer */
                .vikraya-page .gb-res { margin-top: 12px; border-radius: 12px; overflow: hidden; border: 1px solid #1e293b; }
                .vikraya-page .gb-res-bar { display: flex; align-items: center; gap: 12px; padding: 8px 13px; background: #111827; }
                .vikraya-page .gb-res-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
                .vikraya-page .gb-res.is-ok .gb-res-dot { background: #34d399; box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.2); }
                .vikraya-page .gb-res.is-err .gb-res-dot { background: #fb7185; box-shadow: 0 0 0 3px rgba(251, 113, 133, 0.2); }
                .vikraya-page .gb-res-code { font-size: 0.74rem; font-weight: 800; letter-spacing: 0.04em; font-family: ui-monospace, Menlo, Consolas, monospace; }
                .vikraya-page .gb-res.is-ok .gb-res-code { color: #6ee7b7; }
                .vikraya-page .gb-res.is-err .gb-res-code { color: #fda4af; }
                .vikraya-page .gb-res-meta { display: inline-flex; align-items: center; gap: 5px; font-size: 0.7rem; color: #94a3b8; }
                .vikraya-page .gb-res-meta i { font-size: 0.68rem; }
                .vikraya-page .gb-res-spacer { flex: 1; }
                .vikraya-page .gb-copy { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 7px; cursor: pointer;
                    background: rgba(148, 163, 184, 0.14); border: 1px solid rgba(148, 163, 184, 0.2); color: #cbd5e1; font-size: 0.68rem; font-weight: 600; transition: background-color .15s ease; }
                .vikraya-page .gb-copy:hover { background: rgba(148, 163, 184, 0.26); color: #fff; }
                .vikraya-page .gb-res-body { margin: 0; padding: 13px 15px; background: #0f172a; color: #e2e8f0; overflow: auto;
                    font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.76rem; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }

                /* Empty detail state */
                .vikraya-page .gb-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; text-align: center; padding: 40px; }
                .vikraya-page .gb-empty-icon { width: 88px; height: 88px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
                    background: linear-gradient(135deg, #fffbeb, #fef3c7); margin-bottom: 18px; }
                .vikraya-page .gb-empty-icon i { font-size: 2.2rem; color: #fbbf24; }
                .vikraya-page .gb-empty h4 { margin: 0 0 6px; font-size: 1.02rem; font-weight: 700; color: #475569; }
                .vikraya-page .gb-empty p { margin: 0; font-size: 0.84rem; color: #94a3b8; max-width: 380px; line-height: 1.55; }

                @media (max-width: 1100px) {
                    .vikraya-page .gb-split { flex-direction: column; height: auto; }
                    .vikraya-page .gb-sidebar { width: 100%; max-height: 360px; }
                    .vikraya-page .gb-detail { min-height: 440px; }
                }
                @media (max-width: 640px) {
                    .vikraya-page { padding: 0.75rem; }
                    .vikraya-page .gb-hero { padding: 18px; }
                    .vikraya-page .gb-stat { flex: 1; min-width: 78px; }
                    .vikraya-page .gb-reqbar { flex-direction: column; align-items: stretch; }
                    .vikraya-page .gb-reqbar-method { border-right: none; border-bottom: 1px solid #e2e5ef; padding: 8px 14px; }
                    .vikraya-page .gb-reqbar-action .p-button { width: 100%; }
                    .vikraya-page .gb-hide-sm { display: none; }
                    .vikraya-page .gb-detail-header, .vikraya-page .gb-detail-body { padding-left: 16px; padding-right: 16px; }
                }
            `}</style>

            {/* ── Hero ───────────────────────────────────────────── */}
            <div className="gb-hero">
                <div className="gb-hero-main">
                    <div className="gb-hero-icon">
                        <i className="pi pi-shopping-cart" />
                    </div>
                    <div>
                        <h2 className="gb-hero-title">Vikraya App — Endpoint Tester</h2>
                        <p className="gb-hero-sub">
                            Select a client and branch, then pick an endpoint on the left — request URL, body and live response appear on the right. Every route here is Client\Vikraya, so it
                            is always served from that client's own deployment (base_url).
                        </p>
                    </div>
                </div>

                <div className="gb-stats">
                    <div className="gb-stat ok">
                        <div className="gb-stat-value">
                            <i className="pi pi-check-circle" />
                            {successCount}
                        </div>
                        <div className="gb-stat-label">Success</div>
                    </div>
                    <div className="gb-stat err">
                        <div className="gb-stat-value">
                            <i className="pi pi-times-circle" />
                            {failCount}
                        </div>
                        <div className="gb-stat-label">Failed</div>
                    </div>
                    <div className="gb-stat idle">
                        <div className="gb-stat-value">
                            <i className="pi pi-minus-circle" />
                            {notTested}
                        </div>
                        <div className="gb-stat-label">Not Tested</div>
                    </div>
                    <div className="gb-stat all">
                        <div className="gb-stat-value">
                            <i className="pi pi-bolt" />
                            {ENDPOINTS.length}
                        </div>
                        <div className="gb-stat-label">Endpoints</div>
                        <div className="gb-progress">
                            <span style={{ width: `${coverage}%` }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Toolbar ────────────────────────────────────────── */}
            <div className="gb-toolbar">
                <div className="gb-field">
                    <label className="gb-field-label">
                        <i className="pi pi-building" /> Client
                    </label>
                    <Dropdown
                        value={selectedClient}
                        options={clients}
                        onChange={(e) => {
                            const row = clients.find((c) => c.value === e.value);
                            setSelectedClient(e.value);
                            setSelectedBranch(null);
                            setBranches([]);
                            setClientBaseUrl(row?.base_url || '');
                            fetchBranches(e.value);
                        }}
                        placeholder="Select Client"
                        filter
                        className="w-full"
                    />
                </div>

                <div className="gb-field">
                    <label className="gb-field-label">
                        <i className="pi pi-sitemap" /> Branch <span className="gb-muted">(segment)</span>
                        {branchesLoading && <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.75rem' }} />}
                    </label>
                    <Dropdown
                        value={selectedBranch}
                        options={branches}
                        onChange={(e) => setSelectedBranch(e.value)}
                        placeholder={branchesLoading ? 'Loading branches…' : 'Select Branch'}
                        filter
                        disabled={!selectedClient || branchesLoading}
                        className="w-full"
                    />
                </div>

                <div className="flex gap-2 align-items-center" style={{ paddingBottom: 1 }}>
                    {selectedBranch && (
                        <span className="gb-segment gb-hide-sm">
                            <i className="pi pi-link" />
                            {currentSegment()}
                        </span>
                    )}
                    <Button
                        label="Clear Results"
                        icon="pi pi-refresh"
                        className="gb-clear"
                        onClick={() => {
                            setResults({});
                            setPostResults({});
                            setPostBody({});
                            setExtraParams({});
                            setUploadFile(null);
                            setUploadExtraFields('{}');
                            setSelectedClient(null);
                            setSelectedBranch(null);
                            setBranches([]);
                            setClientBaseUrl('');
                        }}
                    />
                </div>
            </div>

            {/* ── Split view ─────────────────────────────────────── */}
            <div className="gb-split">
                {/* Left: endpoint list */}
                <div className="gb-sidebar">
                    <div className="gb-search">
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search endpoints…" />
                        </span>
                    </div>

                    {Object.keys(groupedEndpoints).length === 0 && (
                        <div className="gb-empty-search">
                            <i className="pi pi-inbox" style={{ fontSize: '1.6rem', display: 'block', marginBottom: 8, color: '#cbd5e1' }} />
                            No endpoints match your search.
                        </div>
                    )}

                    {Object.entries(groupedEndpoints).map(([group, eps]) => {
                        const gs = groupStyle(group);
                        const isCollapsed = collapsedGroups.has(group);
                        return (
                            <div key={group}>
                                <div className="gb-group-header" style={{ background: gs.bg, color: gs.color, borderLeft: `4px solid ${gs.accent}` }} onClick={() => toggleGroup(group)}>
                                    <i className={gs.icon} />
                                    <span style={{ flex: 1 }}>{group}</span>
                                    <span className="gb-group-count">{eps.length}</span>
                                    <i className={`pi ${isCollapsed ? 'pi-chevron-right' : 'pi-chevron-down'}`} style={{ fontSize: '0.65rem' }} />
                                </div>
                                {!isCollapsed &&
                                    eps.map((ep) => {
                                        const info = statusInfo(ep);
                                        const isSelected = selectedEndpoint?.key === ep.key;
                                        const r = postResults[ep.key] || results[ep.key];
                                        return (
                                            <div key={ep.key} className={`gb-item${isSelected ? ' selected' : ''}`} style={{ borderLeftColor: isSelected ? gs.accent : 'transparent' }} onClick={() => selectEndpoint(ep)}>
                                                <div className="gb-item-row">
                                                    <Pill tone={ep.method.toLowerCase()}>{ep.method}</Pill>
                                                    <span className="gb-item-title">{ep.label}</span>
                                                    {(runningKeys.has(ep.key) || postLoading[ep.key]) && <i className="pi pi-spin pi-spinner" style={{ color: gs.accent, fontSize: '0.8rem' }} />}
                                                </div>
                                                <div className="gb-item-foot">
                                                    <Pill tone={info.severity}>{info.text}</Pill>
                                                    <span className="gb-item-meta">{r ? `${r.duration} ms · ${r.testedAt}` : 'Not tested yet'}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        );
                    })}
                </div>

                {/* Right: detail pane */}
                <div className="gb-detail">
                    {selectedEndpoint ? (
                        <>
                            <div className="gb-detail-header" style={{ background: `linear-gradient(180deg, ${detailStyle.soft} 0%, rgba(255,255,255,0.9) 100%)` }}>
                                <div className="gb-crumb" style={{ color: detailStyle.color }}>
                                    <i className={detailStyle.icon} style={{ fontSize: '0.75rem' }} />
                                    <span>{selectedEndpoint.group}</span>
                                </div>
                                <div className="gb-detail-title">
                                    <Pill tone={selectedEndpoint.method.toLowerCase()}>{selectedEndpoint.method}</Pill>
                                    <h3>{selectedEndpoint.label}</h3>
                                </div>
                                <div className="gb-detail-path">/{selectedEndpoint.path}</div>
                            </div>
                            <div className="gb-detail-body">{renderDetailBody()}</div>
                        </>
                    ) : (
                        <div className="gb-empty">
                            <div className="gb-empty-icon">
                                <i className="pi pi-send" />
                            </div>
                            <h4>No endpoint selected</h4>
                            <p>Pick an endpoint from the list to view its request URL, run it, and inspect the response.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
